// 受注数/納品予定の列構築と日付ヘッダ生成をまとめる。
export function createDemandGridColumnBuilder(deps) {
    const {
        griddataRefs,
        entrydataRef,
        isEmpty,
        isEnabled,
        DicVal,
        resolveGridIndex,
        ensureGridRefArray,
        findEntryValue,
        normalizeColumnIdUpper,
        normalizeColumnIdSuffix,
        normalizeLabelKey,
        isGridAuxColId,
        resolveTypeColumnIdBySuffix,
        resolveTypeColumnIdByName,
        demandTypeKeyByPanel,
        demandJutyuDisplayFieldId,
        isDemandCellEditableByLayout,
        resolveDemandCellStyle,
        NOUHIN_SUMMARY_COL_ID,
        // 任意: グリッド行の取得方法が異なる画面向け(未指定なら griddataRefs から引く)
        getGridRows
    } = deps;

    // 納品予定パネルか判定する。
    function isNouhinYoteiPanel(panelInfo) {
        return demandTypeKeyByPanel(panelInfo) === "131";
    }

    // 納品予定パネルの明細データを取得する。
    function getNouhinRows(panelInfo) {
        if (isEmpty(panelInfo)) return [];
        if (typeof getGridRows === "function") {
            const rows = getGridRows(panelInfo);
            return Array.isArray(rows) ? rows : [];
        }
        const idx = resolveGridIndex(panelInfo);
        if (idx === -1) return [];
        const dref = ensureGridRefArray(griddataRefs, idx);
        if (!Array.isArray(dref.current)) return [];
        return dref.current;
    }

    // 納品予定の上段日付ラベルを取得する。
    function formatNouhinDateLabel(raw) {
        const s = String(raw ?? "").trim();
        if (s === "") return "";
        const ymd = s.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})(?:\s+\d{1,2}:\d{1,2}(?::\d{1,2})?)?$/);
        if (!isEmpty(ymd)) {
            const m = Number(ymd[2]);
            const d = Number(ymd[3]);
            if (Number.isFinite(m) && Number.isFinite(d)) return m + "月" + d + "日";
        }
        const isoDateTime = s.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})T\d{1,2}:\d{1,2}(?::\d{1,2})?$/);
        if (!isEmpty(isoDateTime)) {
            const m = Number(isoDateTime[2]);
            const d = Number(isoDateTime[3]);
            if (Number.isFinite(m) && Number.isFinite(d)) return m + "月" + d + "日";
        }
        const ymd8 = s.match(/^(\d{4})(\d{2})(\d{2})$/);
        if (!isEmpty(ymd8)) {
            const m = Number(ymd8[2]);
            const d = Number(ymd8[3]);
            if (Number.isFinite(m) && Number.isFinite(d)) return m + "月" + d + "日";
        }
        return s;
    }

    // 納品予定ヘッダの代表日付ラベルを算出する。
    function nouhinDateLabel(panelInfo) {
        const rows = getNouhinRows(panelInfo);
        const first = rows.length > 0 ? rows[0] : {};
        const entryDay = String(findEntryValue(entrydataRef.current, [
            "SSDNY_NOUHINYOTEI"
        ]) ?? "");
        const day1 = String(first.SSDNY_NOUHINYOTEI || first.SSDNY_PART1 || "");
        const day2 = String(first.SSDNY_PART2 || "");
        const day3 = String(first.SSDNY_PART3 || "");
        const day4 = String(first.SSDNY_PART4 || "");
        const raw = [entryDay, day1, day2, day3, day4].find((v) => !isEmpty(v)) || "";
        return formatNouhinDateLabel(raw);
    }

    // 納品予定: 行データ候補から最初の非空日付を取得する。
    function firstNouhinDateFromRows(rows, candidates) {
        const list = Array.isArray(rows) ? rows : [];
        const keys = Array.isArray(candidates) ? candidates : [];
        for (let i = 0; i < list.length; i++) {
            const row = list[i] || {};
            for (let j = 0; j < keys.length; j++) {
                const v = String(DicVal(row, keys[j], "") ?? "").trim();
                if (!isEmpty(v)) return v;
            }
        }
        return "";
    }

    // 納品予定: 各数量列の上段に出す日付ラベルを作成する。
    function buildNouhinDateHeaderMap(panelInfo) {
        const rows = getNouhinRows(panelInfo);
        const byColIdUpper = {};
        const setLabel = (colId, rawDate) => {
            if (isEmpty(colId)) return;
            const key = normalizeColumnIdUpper(colId);
            if (key === "") return;
            byColIdUpper[key] = formatNouhinDateLabel(rawDate);
        };
        const siireDate = String(findEntryValue(entrydataRef.current, ["SSRM_SIIREBI"]) ?? "");
        const nouhinDate = String(findEntryValue(entrydataRef.current, ["SSDNY_NOUHINYOTEI"]) ?? "");
        const centerDate = String(findEntryValue(entrydataRef.current, ["SSDNY_CENTER"]) ?? "");
        const part1Date = String(findEntryValue(entrydataRef.current, ["SSDNY_PART1"]) ?? "");
        const part2Date = String(findEntryValue(entrydataRef.current, ["SSDNY_PART2"]) ?? "");
        const part3Date = String(findEntryValue(entrydataRef.current, ["SSDNY_PART3"]) ?? "");
        const part4Date = String(findEntryValue(entrydataRef.current, ["SSDNY_PART4"]) ?? "");
        const fallbackSiire = firstNouhinDateFromRows(rows, ["SSRM_SIIREBI"]);
        const fallbackNouhin = firstNouhinDateFromRows(rows, ["SSDNY_NOUHINYOTEI"]);
        const fallbackCenter = firstNouhinDateFromRows(rows, ["SSDNY_CENTER"]);
        const fallbackPart1 = firstNouhinDateFromRows(rows, ["SSDNY_PART1"]);
        const fallbackPart2 = firstNouhinDateFromRows(rows, ["SSDNY_PART2"]);
        const fallbackPart3 = firstNouhinDateFromRows(rows, ["SSDNY_PART3"]);
        const fallbackPart4 = firstNouhinDateFromRows(rows, ["SSDNY_PART4"]);

        // 仕様: _SU は数量列。上段に日付（*_SIIREBI / *_NOUHINYOTEI / *_CENTER / *_PART1..4）を表示する。
        const siireQtyCol = resolveTypeColumnIdBySuffix("131", ["siiresu"]) || resolveTypeColumnIdByName("131", ["仕入実績", "仕入実績数"]);
        const nouhinQtyCol = resolveTypeColumnIdBySuffix("131", ["nouhinyoteisu"]) || resolveTypeColumnIdByName("131", ["納品予定", "納品予定数"]);
        const centerQtyCol = resolveTypeColumnIdBySuffix("131", ["centersu"]) || resolveTypeColumnIdByName("131", ["センター納品", "センター納品数"]);
        const part1QtyCol = resolveTypeColumnIdBySuffix("131", ["part1su"]) || resolveTypeColumnIdByName("131", ["分納1", "分納1数"]);
        const part2QtyCol = resolveTypeColumnIdBySuffix("131", ["part2su"]) || resolveTypeColumnIdByName("131", ["分納2", "分納2数"]);
        const part3QtyCol = resolveTypeColumnIdBySuffix("131", ["part3su"]) || resolveTypeColumnIdByName("131", ["分納3", "分納3数"]);
        const part4QtyCol = resolveTypeColumnIdBySuffix("131", ["part4su"]) || resolveTypeColumnIdByName("131", ["分納4", "分納4数"]);

        setLabel(siireQtyCol, isEmpty(siireDate) ? fallbackSiire : siireDate);
        setLabel(nouhinQtyCol, isEmpty(nouhinDate) ? fallbackNouhin : nouhinDate);
        setLabel(centerQtyCol, isEmpty(centerDate) ? fallbackCenter : centerDate);
        setLabel(part1QtyCol, isEmpty(part1Date) ? fallbackPart1 : part1Date);
        setLabel(part2QtyCol, isEmpty(part2Date) ? fallbackPart2 : part2Date);
        setLabel(part3QtyCol, isEmpty(part3Date) ? fallbackPart3 : part3Date);
        setLabel(part4QtyCol, isEmpty(part4Date) ? fallbackPart4 : part4Date);
        return byColIdUpper;
    }

    // 納品予定: 列ヘッダを2段化（上段は日付帯、下段は通常ヘッダ）。
    function buildNouhinHeaderColumns(colDef, panelInfo) {
        if (!Array.isArray(colDef)) return colDef;
        if (!isNouhinYoteiPanel(panelInfo)) return colDef;
        const dateHeaderMap = buildNouhinDateHeaderMap(panelInfo);
        const defaultLabel = nouhinDateLabel(panelInfo);
        return colDef.map((col) => {
            if (isEmpty(col)) return col;
            if (!isEmpty(col.children)) return col;
            const colId = String(DicVal(col, "colId", ""));
            if (isGridAuxColId(colId)) return col;
            const up = normalizeColumnIdUpper(colId);
            const parentHeader = DicVal(dateHeaderMap, up, "");
            const topLabel = isEmpty(parentHeader) ? (up === "SSDNY_NOUHINYOTEI_SU" ? defaultLabel : " ") : parentHeader;
            return {
                headerName: topLabel,
                headerClass: "kakou-nouhin-date-header",
                children: [{ ...col, suppressSpanHeaderHeight: true }]
            };
        });
    }

    // 受注数/納品予定: 列グループを外して単純列へ平坦化する。
    function flattenDemandColumns(colDef) {
        if (!Array.isArray(colDef)) return [];
        const out = [];
        const pushCol = (col) => {
            if (isEmpty(col)) return;
            if (Array.isArray(col.children) && col.children.length !== 0) {
                col.children.forEach((ch) => pushCol(ch));
                return;
            }
            out.push({ ...col });
        };
        colDef.forEach((c) => pushCol(c));
        return out;
    }

    // 受注数/納品予定: system列をそのまま出さず、表示対象列のみへ絞る。
    function buildDemandColumns(colDef, panelInfo) {
        if (!Array.isArray(colDef)) return [];
        if (isEmpty(panelInfo)) return colDef;
        const demandType = demandTypeKeyByPanel(panelInfo);
        const isJutyu = demandType === "130";
        const isNouhin = demandType === "131";
        if (!isJutyu && !isNouhin) return colDef;

        const jutyuSuffix = ["colorcd", "sizecd", "jyucyusu", "tokuisakicd", "torikeitaisyosaikbn"];
        const nouhinSuffix = ["colorcd", "sizecd", "mdhatchusu", "siiresu", "nouhinyoteisu", "centersu", "part1su", "part2su", "part3su", "part4su", "zansu"];
        const keepSuffix = isJutyu ? jutyuSuffix : nouhinSuffix;

        const flatCols = flattenDemandColumns(colDef);
        const originalVisibleCols = flatCols.filter((col) => {
            if (isEmpty(col)) return false;
            const cid = String(DicVal(col, "colId", ""));
            if (cid === "" || isGridAuxColId(cid)) return false;
            if (!isEmpty(col.children)) return false;
            return !isEnabled(col.hide);
        });
        const filtered = flatCols.filter((col) => {
            if (isEmpty(col)) return false;
            const cid = String(DicVal(col, "colId", ""));
            const headerNm = String(DicVal(col, "headerName", DicVal(col, "columnnm", "")));
            if (isGridAuxColId(cid)) return false;
            const headerKey = normalizeLabelKey(headerNm);
            if (isNouhin && headerKey === "残数") return true;
            if (isNouhin && headerKey.endsWith("計")) return false;
            const sfx = normalizeColumnIdSuffix(cid);
            if (sfx === "") return false;
            return keepSuffix.includes(sfx);
        });

        if (filtered.length === 0) {
            return colDef;
        }
        const restored = filtered.map((col) => {
            if (isEmpty(col)) return col;
            const cid = String(DicVal(col, "colId", ""));
            if (cid === "" || isGridAuxColId(cid)) return col;
            const originalCellStyle = DicVal(col, "cellStyle", null);
            const originalValueFormatter = DicVal(col, "valueFormatter", null);
            const displayOnlyFieldId = isJutyu ? demandJutyuDisplayFieldId(cid) : "";
            const isShopChannelColumn = displayOnlyFieldId !== "";
            const isZansuColumn = isNouhin && normalizeColumnIdSuffix(cid) === "zansu";
            const isNouhinQuantityColumn = isNouhin && [
                "mdhatchusu", "siiresu", "nouhinyoteisu", "centersu",
                "part1su", "part2su", "part3su", "part4su", "zansu"
            ].includes(normalizeColumnIdSuffix(cid));
            const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj ?? {}, key);
            const readDemandValue = (params) => {
                const row = DicVal(params, "data", {});
                const candidateKeys = [
                    DicVal(col, "field", cid),
                    cid
                ].filter((key) => String(key ?? "").trim() !== "");
                if (isNouhin && isZansuColumn) {
                    candidateKeys.push(NOUHIN_SUMMARY_COL_ID.zansuTotal, NOUHIN_SUMMARY_COL_ID.zansu);
                }
                for (let i = 0; i < candidateKeys.length; i++) {
                    const key = String(candidateKeys[i] ?? "").trim();
                    if (key === "" || !hasOwn(row, key)) continue;
                    const value = DicVal(row, key, null);
                    if (value !== null && value !== undefined) return value;
                }
                return DicVal(params, "value", "");
            };
            const formatDemandQuantityValue = (value, fallbackZero = false) => {
                const text = String(value ?? "").trim();
                if (text === "") return fallbackZero ? "0" : "";
                const num = Number(text.replace(/,/g, ""));
                if (!Number.isFinite(num)) return text;
                return String(Math.round(num));
            };
            return {
                ...col,
                field: isShopChannelColumn ? displayOnlyFieldId : DicVal(col, "field", cid),
                hide: false,
                initialHide: false,
                flex: undefined,
                suppressSizeToFit: true,
                minWidth: isShopChannelColumn ? Math.max(Number(DicVal(col, "minWidth", 0)) || 0, 110) : DicVal(col, "minWidth", undefined),
                // 受注納品の計行・小計行は表示専用（保存対象外）のため編集不可にする。
                editable: isShopChannelColumn ? false : ((params) => isDemandCellEditableByLayout(col, params)),
                valueFormatter: (params) => {
                    const rowKind = String(DicVal(DicVal(params, "data", {}), "row_kind", "")).trim();
                    const rawValue = DicVal(params, "value", "");
                    if (isNouhin && rowKind === "summary_label") {
                        return rawValue == null ? "" : String(rawValue);
                    }
                    if (isNouhin && rowKind === "summary_total" && isNouhinQuantityColumn) {
                        return formatDemandQuantityValue(readDemandValue(params), true);
                    }
                    if (isNouhin && isNouhinQuantityColumn) {
                        return formatDemandQuantityValue(readDemandValue(params), true);
                    }
                    if (typeof originalValueFormatter === "function") {
                        const formatted = originalValueFormatter(params);
                        return formatted == null ? "" : String(formatted);
                    }
                    return rawValue == null ? "" : String(rawValue);
                },
                cellStyle: (params) => resolveDemandCellStyle(originalCellStyle, col, params, demandType)
            };
        });
        const dataCols = restored.filter((col) => {
            const cid = String(DicVal(col, "colId", ""));
            return cid !== "" && !isGridAuxColId(cid) && isEmpty(col.children);
        });
        const visibleCols = dataCols.filter((col) => !isEnabled(col.hide));
        // 想定外定義で全消しになる場合は元列を維持して表示不能化を防ぐ。
        if (dataCols.length === 0) return colDef;
        if (visibleCols.length === 0 && originalVisibleCols.length !== 0) return colDef;
        return restored;
    }

    return {
        isNouhinYoteiPanel,
        buildNouhinHeaderColumns,
        buildDemandColumns
    };
}
